import React, { useContext, useEffect, useMemo, useState } from 'react'
import linkHeader from 'http-link-header'
import { useLocation } from 'react-router-dom'

export const CinemaModeContext = React.createContext(null);

export function CinemaModeProvider({ children }) {
  const [cinemaMode, setCinemaMode] = useState(() => localStorage.getItem("cinema-mode") === "true");
  const state = useMemo(() => ({
    cinemaMode,
    setCinemaMode,
    toggleCinemaMode: () => setCinemaMode((prev) => !prev),
  }), [cinemaMode, setCinemaMode]);

  useEffect(() => localStorage.setItem("cinema-mode", cinemaMode), [cinemaMode]);
  return (
    <CinemaModeContext.Provider value={state}>
      {children}
    </CinemaModeContext.Provider>
  );
}

function PlayerPage() {
  const { cinemaMode, toggleCinemaMode } = useContext(CinemaModeContext);
  return (
    <div className={`flex flex-col items-center ${!cinemaMode && 'mx-auto px-2 py-2 container'}`}>
      <Player cinemaMode={cinemaMode} />
      <button className='bg-blue-900 px-4 py-2 rounded-lg mt-6' onClick={toggleCinemaMode}>
        {cinemaMode ? "Disable cinema mode" : "Enable cinema mode"}
      </button>
    </div>
  )
}

function Player({ cinemaMode }) {
  const videoRef = React.createRef()
  const location = useLocation()
  const [videoLayers, setVideoLayers] = React.useState([]);
  const [mediaSrcObject, setMediaSrcObject] = React.useState(null);
  const [layerEndpoint, setLayerEndpoint] = React.useState('');

  const onLayerChange = event => {
    fetch(layerEndpoint, {
      method: 'POST',
      body: JSON.stringify({ mediaId: '1', encodingId: event.target.value }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = mediaSrcObject
    }
  }, [mediaSrcObject, videoRef])

  React.useEffect(() => {
    const peerConnection = new RTCPeerConnection() // eslint-disable-line

    peerConnection.ontrack = function (event) {
      setMediaSrcObject(event.streams[0])
    }

    peerConnection.addTransceiver('audio', { direction: 'recvonly' })
    peerConnection.addTransceiver('video', { direction: 'recvonly' })

    peerConnection.createOffer().then(offer => {
      offer["sdp"] = offer["sdp"].replace("useinbandfec=1", "useinbandfec=1;stereo=1")
      peerConnection.setLocalDescription(offer)

      fetch(`${process.env.REACT_APP_API_PATH}/whep`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${location.pathname.substring(1)}`,
          'Content-Type': 'application/sdp'
        }
      }).then(r => {
        return new Promise(resolve => {
          r.text().then(answer => {
            const parsedLinkHeader = new linkHeader(r.headers.get('Link'))
            setLayerEndpoint(`${window.location.protocol}//${parsedLinkHeader.refs.find(l => l.rel === 'urn:ietf:params:whep:ext:core:layer').uri}`)

            const evtSource = new EventSource(`${window.location.protocol}//${parsedLinkHeader.refs.find(l => l.rel === 'urn:ietf:params:whep:ext:core:server-sent-events').uri}`)
            evtSource.onerror = err => evtSource.close();

            evtSource.addEventListener("layers", event => {
              const parsed = JSON.parse(event.data)
              setVideoLayers(parsed['1']['layers'].map(l => l.encodingId))
            })

            let iceServers = parsedLinkHeader.refs
              .filter(l => l.rel === 'ice-server')
              .map(i => {
                i.urls = i.uri
                return i
              })

            if (iceServers.length !== 0) {
              peerConnection.setConfiguration({
                iceServers,
              })
              peerConnection.createOffer().then(offer => {
                peerConnection.setLocalDescription(offer)
                resolve(answer)
              })
            } else {
              resolve(answer)
            }
          })
        })
      }).then(answer => {
        peerConnection.setRemoteDescription({
          sdp: answer,
          type: 'answer'
        })
      })
    })

    return function cleanup() {
      peerConnection.close()
    }
  }, [location.pathname])

  return (
    <>
      <video
        ref={videoRef}
        autoPlay
        muted
        controls
        playsInline
        className={`bg-black w-full ${cinemaMode && "min-h-screen"}`}
      />

      {videoLayers.length >= 2 &&
        <select defaultValue="disabled" onChange={onLayerChange} className="appearance-none border w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline bg-gray-700 border-gray-700 text-white rounded shadow-md placeholder-gray-200">
          <option value="disabled" disabled={true}>Choose Quality Level</option>
          {videoLayers.map(layer => {
            return <option key={layer} value={layer}>{layer}</option>
          })}
        </select>
      }
    </>
  )
}

export default PlayerPage
